import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { RegexTypeEnum } from '@shared/types';

enum FormSubmitResult {
    Invalid,
    Valid,
}

function onFormSubmit(form: FormGroup): FormSubmitResult {
    form.markAllAsTouched();

    if (form.invalid) {
        return FormSubmitResult.Invalid;
    }

    form.disable();
    return FormSubmitResult.Valid;
}

function onFormComplete(form: FormGroup) {
    form.enable();
}

function onFormReset(form: FormGroup) {
    form.enable();
    form.reset();
}

function email(control: AbstractControl): ValidationErrors | null {
    const emailRegex = new RegExp(RegexTypeEnum.EMAIL_PATTERN);
    const isValid = emailRegex.test(control.value);

    return isValid ? null : { invalidEmail: true };
}

function emailsArray(control: AbstractControl): ValidationErrors | null {
    const emails = control.value as string[];
    const emailRegex = new RegExp(RegexTypeEnum.EMAIL_PATTERN);
    const invalidEmails = emails.filter(email => !emailRegex.test(email.trim()));

    return invalidEmails.length > 0 ? { invalidEmails: invalidEmails } : null;
}

function password(): ValidatorFn {
    return (control: AbstractControl): Record<string, any> | null => {
        const value = control.value;

        if (!value) {
            return null; // return if empty, as required validator should handle this case
        }

        const hasUpperCase = /[A-Z]/.test(value);
        const hasLowerCase = /[a-z]/.test(value);
        const hasNumber = /\d/.test(value);
        const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>+=\-[\];~`]/.test(value);
        const hasMinLength = value.length >= 8;

        const valid = hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && hasMinLength;

        return valid ? null : { passwordStrength: true };
    };
}

function name(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const regex = /^[A-Za-z'\s-]+$/;

        const isValid = regex.test(control.value);

        return isValid ? null : { invalidName: true };
    };
}

function match(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): Record<string, boolean> | null => {
        const control = formGroup.get(controlName);
        const matchingControl = formGroup.get(matchingControlName);

        if (!control || !matchingControl) {
            return null;
        }

        if (matchingControl.errors && !matchingControl.errors['mismatch']) {
            return null;
        }

        // set error on matchingControl if validation fails
        if (control.value !== matchingControl.value) {
            matchingControl.setErrors({ mismatch: true });
            return { mismatch: true };
        } else {
            matchingControl.setErrors(null);
            return null;
        }
    };
}

function notMatch(controlName: string, matchingControlName: string): ValidatorFn {
    return (formGroup: AbstractControl): Record<string, boolean> | null => {
        const control = formGroup.get(controlName);
        const matchingControl = formGroup.get(matchingControlName);

        if (!control || !matchingControl) {
            return null;
        }

        if (!control.value || !matchingControl.value) {
            if (matchingControl.errors?.['notMatch']) {
                const errors = { ...matchingControl.errors };
                delete errors['notMatch'];
                matchingControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
            }
            return null;
        }

        const controlValue = String(control.value).trim();
        const matchingControlValue = String(matchingControl.value).trim();

        if (controlValue === matchingControlValue) {
            matchingControl.setErrors({ ...matchingControl.errors, notMatch: true });
            return { notMatch: true };
        } else {
            if (matchingControl.errors?.['notMatch']) {
                const errors = { ...matchingControl.errors };
                delete errors['notMatch'];
                matchingControl.setErrors(Object.keys(errors).length > 0 ? errors : null);
            }
            return null;
        }
    };
}

function emptyInput(inputValue: FormControl | AbstractControl): ValidationErrors | null {
    if (!inputValue.value) {
        return null;
    }
    return inputValue!.value.trim() !== '' ? null : { emptyInput: true };
}

function emptyStringOrObject(inputValue: FormControl | AbstractControl): ValidationErrors | null {
    if (typeof inputValue.value === 'string') {
        return inputValue!.value?.trim() !== '' ? null : { emptyInput: true };
    } else {
        return inputValue!.value?.value?.trim() !== '' ? null : { emptyInput: true };
    }
}

function dateRangeYear(minYear?: number, maxYear?: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const date = new Date(control.value);
        const year = date.getFullYear();

        // If minYear is provided, check if the date is before it
        if (minYear !== undefined && year < minYear) {
            return { minDate: { min: minYear, actual: year } };
        }

        // If maxYear is provided, check if the date is after it
        if (maxYear !== undefined && year > maxYear) {
            return { maxDate: { max: maxYear, actual: year } };
        }

        return null;
    };
}

function nullUndefined(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    return value === null || value === undefined ? { required: true } : null;
}

function quillRequired(control: AbstractControl): ValidationErrors | null {
    const quillText = control.value
        ?.replace(/<(.|\n)*?>/g, '')
        .replace(/&nbsp;/g, ' ')
        .trim();
    if (!quillText) {
        return { required: true };
    } else {
        return null;
    }
}

function quillMaxLength(maxLength: number): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) {
            return null;
        }

        const textWithoutHtml = control.value
            .replace(/<[^>]*>/g, '')
            .trim()
            .replace(/&nbsp;/g, ' ')
            .replace(/&lt;/g, '<')
            .replace(/&gt;/g, '>')
            .replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&')
            .replace(/&#39;/g, `'`);

        if (textWithoutHtml.length > maxLength) {
            return {
                maxlength: {
                    requiredLength: maxLength,
                    actualLength: textWithoutHtml.length,
                },
            };
        }

        return null;
    };
}

function happyFoxTicketId(control: AbstractControl): ValidationErrors | null {
    if (!control.value) {
        return null;
    }

    const value = control.value.toString().trim();

    if (value.length > 200) {
        return { maxlength: { invalidHappyFoxTicketId: true } };
    }

    const numericValue = value.replace(/\D/g, '');
    if (numericValue !== value || numericValue.length === 0) {
        return { invalidHappyFoxTicketId: true };
    }

    const number = Number(numericValue);
    if (number > 2147483647) {
        return { invalidHappyFoxTicketId: true };
    }

    return null;
}

function probeSequence(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const validCharacters = /^[AGTC]*$/;
        const value = control.value;

        if (value && !validCharacters.test(value)) {
            return { probeSequence: true };
        }
        return null;
    };
}

function primerSequence(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const value = control.value;

        if (!value) {
            return null;
        }

        const trimmedValue = value.trim();

        if (!/^[AGTCagtc]+$/.test(trimmedValue)) {
            return { invalidPrimerSequenceCharacters: true };
        }

        return null;
    };
}

function primerSequenceDistinct(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        const fwdPrimer = formGroup.get('forwardPrimerSequence')?.value;
        const revPrimer = formGroup.get('reversePrimerSequence')?.value;

        if (fwdPrimer && revPrimer && fwdPrimer.toLowerCase() === revPrimer.toLowerCase()) {
            return { duplicateSequences: true };
        }

        return null;
    };
}

function primerSequenceDistinct2(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        const fwdPrimer2 = formGroup.get('forwardPrimerSequence2')?.value;
        const revPrimer2 = formGroup.get('reversePrimerSequence2')?.value;

        if (fwdPrimer2 && revPrimer2 && fwdPrimer2.toLowerCase() === revPrimer2.toLowerCase()) {
            return { duplicateSequences2: true };
        }

        return null;
    };
}

function requiredTitleOrPubMedIdOrFile(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        const title = formGroup.get('title')?.value?.trim();
        const pubMedId = formGroup.get('pubMedId')?.value?.trim();
        const file = formGroup.get('file')?.value;

        if (!title && !pubMedId && !file) {
            return { requiredAtLeastOne: true };
        }

        return null;
    };
}

function requiredFile(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        const file = formGroup.get('file')?.value;

        if (!file) {
            return { requiredFile: true };
        }

        return null;
    };
}

function requiredDatabaseNameOrNumber(): ValidatorFn {
    return (formGroup: AbstractControl): ValidationErrors | null => {
        const name = formGroup.get('name')?.value;
        const databaseIdNumber = formGroup.get('databaseIdNumber')?.value;

        if (!name && !databaseIdNumber) {
            return { requiredAtLeastOne: true };
        }

        return null;
    };
}

function tubeBarcodeValidator(inputValue: FormControl | AbstractControl): ValidationErrors | null {
    const regex: any = /^((amp|AMP|lv|LV|g|G)[0-9]{4,10})$/;

    const isValid = regex.test(inputValue?.value);

    return isValid ? null : { invalidTubeBarcode: true };
}

function onlyNumbers(inputValue: FormControl | AbstractControl): ValidationErrors | null {
    if (!inputValue.value) {
        return null;
    }

    const onlyNumberRegex = /^[0-9]*$/;
    const value = inputValue.value.toString().trim();
    const isValid = onlyNumberRegex.test(value);

    return isValid ? null : { notOnlyNumbers: true };
}

export const FormHelper = {
    FormSubmitResult,
    onFormSubmit,
    onFormComplete,
    onFormReset,
};

export const FormValidators = {
    password,
    email,
    emailsArray,
    name,
    match,
    notMatch,
    emptyInput,
    emptyStringOrObject,
    dateRangeYear,
    nullUndefined,
    quillRequired,
    quillMaxLength,
    happyFoxTicketId,
    probeSequence,
    primerSequence,
    primerSequenceDistinct,
    primerSequenceDistinct2,
    requiredTitleOrPubMedIdOrFile,
    requiredFile,
    requiredDatabaseNameOrNumber,
    tubeBarcodeValidator,
    onlyNumbers,
};
